import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { initialAdvisorOpportunitiesState, initialState } from './initialState';
import { ApiExError } from '@shared';
import { PriorityStatus } from '@client/util/priorityStatus';

const advisorOpportunitiesSlice = createSlice({
  name: 'advisorOpportunities',
  initialState: initialState.advisorOpportunities,
  reducers: {
    postBatchClientEngagementEmailsRequest(
      state,
      action: PayloadAction<PostBatchClientEngagementEmailsProps>,
    ) {
      const engagementType = action.payload
        .engagementType as ClientEmailEngagement;
      state[engagementType].status = 'sendingEmails';
    },
    postBatchClientEngagementEmailsResponse(
      state,
      action: PayloadAction<BatchEmailReminderResult>,
    ) {
      const engagementType = action.payload.engagementType!;
      state[engagementType].status = 'sendingEmailsComplete';
      state[engagementType].successfulEmails = action.payload.successfulEmails;
      state[engagementType].failedEmails = action.payload.failedEmails;
    },
    postBatchClientEngagementEmailsFailure(
      state,
      action: PayloadAction<ApiExError>,
    ) {
      Object.keys(state).forEach(key => {
        const engagementType = key as unknown as ClientEmailEngagement;
        const reminder = state[engagementType];

        if (reminder.status === 'sendingEmails') {
          reminder.status = 'sendingEmailsError';
          reminder.errorMessage = action.payload.message;
        }
      });
    },
    postQualifyingOpportunityClientsRequest(
      state,
      action: PayloadAction<QualifyingOpportunityClientBodyProps>,
    ) {
      const engagementType = action.payload.engagementType;
      state[engagementType].status = 'fetchingClients';
    },
    postQualifyingOpportunityClientsResponse(
      state,
      action: PayloadAction<QualifyingOpportunityClientsResult>,
    ) {
      const engagementType = action.payload.engagementType;
      state[engagementType].status = 'fetchingClientsComplete';
      state[engagementType].qualifyingClients = action.payload.clients;
    },
    postQualifyingOpportunityClientsFailure(
      state,
      action: PayloadAction<ApiExError>,
    ) {
      Object.keys(state).forEach(key => {
        const engagementType = key as unknown as ClientEmailEngagement;
        const reminder = state[engagementType];

        if (reminder.status === 'fetchingClients') {
          reminder.status = 'fetchingClientsError';
          reminder.errorMessage = action.payload.message;
        }
      });
    },
    resetAdvisorOpportunitiesState(
      state,
      action: PayloadAction<ClientEmailEngagement>,
    ) {
      const engagementType = action.payload;
      state[engagementType] = initialAdvisorOpportunitiesState;
    },
    updatePriorityStatus(
      state,
      action: PayloadAction<{
        engagementType: ClientEmailEngagement;
        priorityStatus: PriorityStatus;
      }>,
    ) {
      const { engagementType, priorityStatus } = action.payload;
      state[engagementType].priorityStatus = priorityStatus;
    },
    updatePriorityStatuses(
      state,
      action: PayloadAction<Record<ClientEmailEngagement, PriorityStatus>>,
    ) {
      const priorityStatuses = action.payload;
      Object.entries(priorityStatuses).forEach(([engagementType, status]) => {
        state[engagementType as ClientEmailEngagement].priorityStatus = status;
      });
    },
  },
});

export const {
  postBatchClientEngagementEmailsRequest,
  postBatchClientEngagementEmailsResponse,
  postBatchClientEngagementEmailsFailure,
  postQualifyingOpportunityClientsRequest,
  postQualifyingOpportunityClientsResponse,
  postQualifyingOpportunityClientsFailure,
  resetAdvisorOpportunitiesState,
  updatePriorityStatus,
  updatePriorityStatuses,
} = advisorOpportunitiesSlice.actions;

export const advisorOpportunitiesReducer = advisorOpportunitiesSlice.reducer;
